/**
 * The folder's panel — an ISLAND that grows out of its rail tile.
 *
 * Geometry: the tile and the panel are one silhouette (rail/islandPath.ts),
 * painted by an SVG behind the content, joined at the tile's right edge with
 * concave fillets. The panel is centred on the tile when the viewport allows
 * and slides up or down around it otherwise; the tile is the rail's and never
 * moves. On top of the SVG: the island's OWN copy of the tile face (closing
 * the folder on click; the combine target when an app is dragged onto the
 * open folder), the title (an input in rename mode) and the member rows.
 *
 * What it does NOT own: the app context menu (the TaskBar's one menu serves
 * every tile, rows included, so the items cannot drift between a rail tile and
 * the same app inside a folder), the drag (rows press through the TaskBar's
 * hook; this component only shifts them and draws the landing ghost when the
 * hook says which slot is open), and persistence (every action is a callback).
 *
 * Rows are a fixed pitch (`FOLDER_ROW_H`) so a drag can find its slot by
 * arithmetic, and the list scrolls past `MAX_ROWS` so a big folder never runs
 * off the viewport — the SVG is sized from the same numbers, so it never has
 * to be measured.
 */
import { useEffect, useLayoutEffect, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { Globe, GripVertical } from 'lucide-react'
import { useTranslation } from '@papan/i18n'
import { useTheme, getThemeClass } from '../ThemeProvider.js'
import { useDismiss } from '../useDismiss.js'
import { MiniappIcon } from '../MiniappIcon.js'
import { FolderGlyph } from './FolderGlyph.js'
import { islandLayout, islandMorph, islandPath, type IslandLayout } from './islandPath.js'
import { RAIL_SHIFT_TRANSITION, prefersReducedMotion } from './spring.js'
import type { RailApp, RailFolder } from './composeRailEntries.js'
import { isKeyboardFocus } from './focus.js'

/**
 * ── How it opens and closes ────────────────────────────────────────────────
 *
 * The SILHOUETTE ITSELF is animated, frame by frame, from the tile out to the
 * full island (`islandMorph` → `islandPath`). Nothing about it is a CSS
 * animation, and that is the point: three attempts at expressing this in CSS
 * each traded one artefact for another —
 *
 *   · a fade+scale of the whole island dimmed the folder's glyph over the
 *     rail's identical glyph underneath, so the tile blinked;
 *   · a `clip-path` wipe stopped the blink and revealed the panel's text
 *     sliced column by column, which reads cheap, and popped the surface in at
 *     full opacity on its first frame;
 *   · scale+fade of everything-but-the-face still starts and ends a composited
 *     layer on a filtered subtree, which is where the last flicker lived.
 *
 * Driving the geometry means there is no second copy of the tile to cross-fade
 * and no layer to promote: at every instant the island is a REAL shape, the
 * tile end of it exactly covering the rail tile it grew from, and the box, the
 * path, the surface and the contents are written from one progress value.
 *
 * The contents fade on a squared ramp late in the open, so text never appears
 * outside a silhouette that has not reached it yet.
 */
const OPEN_MS = 280
const CLOSE_MS = 200
/** Fast out of the gate, long settle — the shape of every macOS expansion. */
const easeOut = (x: number) => 1 - Math.pow(1 - x, 4)
/** Mirrored for the close: it leaves gently and finishes decisively. */
const easeIn = (x: number) => x * x * x

/** Row pitch, shared with the TaskBar's slot arithmetic for this list. */
export const FOLDER_ROW_H = 36
const PANEL_W = 224
const HEADER_H = 30
const PAD = 6
const MAX_ROWS = 10
const TILE_RADIUS = 8      // rounded-lg, the rail tile's own
const PANEL_RADIUS = 12
const FILLET_RADIUS = 10
const VIEWPORT_MARGIN = 8

export interface FolderPopupProps {
  folder: RailFolder
  /** The rail tile's viewport rect — the island's anchor. Captured when the
   *  folder opened and kept through the outro, so the panel never jumps. */
  tileRect: DOMRect
  /** Enter rename mode from inside the panel (double-click on the title). */
  onStartRename?: () => void
  /** Rename mode: the title is an input, committed on Enter / blur. */
  renaming: boolean
  onRename: (title: string) => void
  onRenameCancel: () => void
  /** Returns whether the app was ACTUALLY launched. A press on a member row
   *  may turn out to be a drag, which the host swallows — so the row cannot
   *  assume its click launched anything. */
  onOpenApp: (app: RailApp) => boolean | void
  /** Right-click / long-press on a member row → the TaskBar's icon menu. */
  onAppContextMenu: (app: RailApp, el: HTMLElement) => void
  onClose: () => void
  /** The folder has closed: play the outro, then call `onExited`. */
  closing?: boolean
  onExited?: () => void
  /** A member row can be dragged — out onto the rail, or up and down in here. */
  onMemberPointerDown?: (app: RailApp, e: React.PointerEvent<HTMLButtonElement>) => void
  /** The rail key of the row in flight (dimmed), if it is one of ours. */
  dragSourceKey?: string | null
  /** The pointer is up and the ghost is landing — the row it came from hides
   *  rather than dims, and the make-room shifts stop transitioning. Same
   *  handover the rail tiles make; see TaskBar's `dragSourceState`. */
  dragSettling?: boolean
  /** The slot a dragged row would land in, when this list is the target. */
  gapIndex?: number | null
  /** The dragged item's glyph, drawn translucent in that slot. */
  landingGhost?: ReactNode
  /** An app from elsewhere is resting on the island: its tile face rings. */
  armed?: boolean
  /** The tile face element, for the drag's fly-in when it is the target. */
  tileFaceRef?: (el: HTMLButtonElement | null) => void
}

const ROW_ICON = 24

export function FolderPopup({
  folder, tileRect, renaming, onStartRename, onRename, onRenameCancel, onOpenApp, onAppContextMenu, onClose, closing = false, onExited,
  onMemberPointerDown, dragSourceKey = null, dragSettling = false, gapIndex = null, landingGhost, armed = false, tileFaceRef,
}: FolderPopupProps) {
  const { t } = useTranslation('core-ui')
  const { settings } = useTheme()
  const themeClass = getThemeClass(settings.theme.workspace)
  const panelRef = useRef<HTMLDivElement>(null)
  // Presses on RAIL TILES are not "outside": a press is how a drag onto this
  // island begins, and the tile that opened it toggles it itself. Opening an
  // app from a tile closes the island explicitly (TaskBar). The icon context
  // menu is portaled elsewhere too and must keep the panel open while one of
  // its items is clicked. Anything else — the page, the well's blank space —
  // dismisses.
  useDismiss(panelRef, onClose, { enabled: !closing, openGuard: true, ignoreSelector: `[data-rail-drop^="app:"], [data-rail-drop^="folder:"], [class*="ws-menu"]` })

  // ── Keyboard reach (a11y) ────────────────────────────────────────────
  // The panel is PORTALED to the end of <body>, so DOM order does not follow
  // visual order: Tab from the folder tile went to the NEXT RAIL TILE, never
  // into the panel this dialog opened. Focus therefore has to be moved
  // deliberately, and given back on close.
  //
  // Guarded on `:focus-visible` because a mouse user must NOT have focus
  // yanked into the panel — clicking a button focuses it without matching
  // focus-visible, which is exactly the discriminator we want; a keyboard
  // Enter/Space does match. Wrapped in try/catch for engines without it,
  // where the safe answer is "do nothing".
  const restoreTo = useRef<HTMLElement | null>(null)
  /** Set when a member row launches an app, so the close that follows does not
   *  drag focus back to the rail behind it. */
  const openedApp = useRef(false)
  useEffect(() => {
    // NOT WHILE RENAMING. `RenameInput` focuses and selects itself on mount,
    // and child effects run BEFORE the parent's — so moving focus here landed
    // on a member row a moment later, the input blurred, and blur COMMITS a
    // rename. The box vanished the instant it appeared and the folder could
    // not be renamed at all. The input focusing itself IS the focus move this
    // effect exists to provide, so there is nothing to add in that mode.
    if (closing) return
    // A fresh OPEN — reset the launch flag here, not on mount. The popup is
    // keyed by folder id, so re-opening the SAME folder during its own 200ms
    // outro re-uses the mount (the layout effect clears `closingFolder` rather
    // than swapping the component), and a `openedApp` left true from the
    // previous launch then suppressed focus restore for the new session.
    openedApp.current = false
    if (renaming) return
    const opener = document.activeElement as HTMLElement | null
    if (!isKeyboardFocus(opener)) return
    restoreTo.current = opener
    // The first member row if there is one, else the tile face (which closes).
    const first = panelRef.current?.querySelector<HTMLElement>('[data-folder-list] [tabindex], [data-folder-list] button, button')
    first?.focus()
    return () => {
      // Give focus back on a DISMISS (Escape, the close button, a click
      // outside) and never when the panel closed because an app was LAUNCHED
      // from it — that app should keep the focus.
      //
      // This cannot be decided from `document.activeElement`: the cleanup runs
      // while the panel is still mounted, so on BOTH paths focus is sitting on
      // a member row inside it. The previous test — "is focus still ours?" —
      // was therefore true in both cases and yanked focus back to the rail
      // exactly where its own comment said it must not, which is the reverse of
      // what it claimed to do. `openedApp` records the one path that knows.
      if (openedApp.current) return
      const active = document.activeElement
      const ours = !active || active === document.body || panelRef.current?.contains(active)
      if (ours) restoreTo.current?.focus?.()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [closing])

  // The panel's height is arithmetic, not measurement, so the silhouette is
  // right on the first paint (a measured height would flash the wrong shape).
  const rows = Math.min(folder.members.length, MAX_ROWS)
  const panelHeight = PAD * 2 + HEADER_H + rows * FOLDER_ROW_H
  const [viewportHeight, setViewportHeight] = useState(() => (typeof window === 'undefined' ? 900 : window.innerHeight))
  useEffect(() => {
    const onResize = () => setViewportHeight(window.innerHeight)
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])
  const layout = useMemo(() => islandLayout({
    tileRect, panelWidth: PANEL_W, panelHeight, viewportHeight, margin: VIEWPORT_MARGIN,
    tileRadius: TILE_RADIUS, panelRadius: PANEL_RADIUS, filletRadius: FILLET_RADIUS,
  }), [tileRect, panelHeight, viewportHeight])

  // ── The morph ─────────────────────────────────────────────────────────────
  // One progress value writes the box, the path, the surface and the contents.
  // Straight to the DOM: at 60fps this is a dozen attribute writes a frame, and
  // routing it through React would re-render every member row for each one.
  const shapeRef = useRef<SVGPathElement>(null)
  const svgRef = useRef<SVGSVGElement>(null)
  const faceRef = useRef<HTMLButtonElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)
  const layoutRef = useRef<IslandLayout>(layout)
  layoutRef.current = layout
  const progress = useRef(closing ? 1 : 0)

  const apply = useRef<(p: number) => void>(() => {})
  apply.current = (p: number) => {
    const final = layoutRef.current
    const g = islandMorph(final, p)
    progress.current = p
    const root = panelRef.current
    if (root) {
      root.style.top = `${g.top}px`
      root.style.width = `${g.width}px`
      root.style.height = `${g.height}px`
    }
    const svg = svgRef.current
    if (svg) {
      svg.setAttribute('width', String(g.width))
      svg.setAttribute('height', String(g.height))
      svg.setAttribute('viewBox', `0 0 ${g.width} ${g.height}`)
      // The surface itself fades over the first sliver of the open, so the
      // island's fill and its 1px edge arrive ON the tile rather than in one
      // frame over it — that one-frame arrival is what read as a blink.
      svg.style.opacity = String(Math.min(1, p * 4))
    }
    shapeRef.current?.setAttribute('d', islandPath(g))
    // The face is the tile, and the tile does not move: the box's own top is
    // sliding under it, so the face's offset within the box has to track it.
    if (faceRef.current) faceRef.current.style.top = `${g.tileTop}px`
    const content = contentRef.current
    if (content) {
      // Squared, and late: text must not appear beyond a silhouette that has
      // not grown out to it yet.
      const k = Math.max(0, (p - 0.62) / 0.38)
      content.style.opacity = String(k * k)
      content.style.top = `${final.top - g.top}px`
    }
  }

  // Before the first paint, or the island's first frame is the full shape.
  useLayoutEffect(() => { apply.current(closing ? 1 : 0) }, [])

  useEffect(() => {
    const to = closing ? 0 : 1
    const from = progress.current
    if (from === to) { if (closing) onExited?.(); return }
    if (prefersReducedMotion()) { apply.current(to); if (closing) onExited?.(); return }
    const ms = (closing ? CLOSE_MS : OPEN_MS) * Math.abs(to - from)
    const ease = closing ? easeIn : easeOut
    let raf = 0
    let start = 0
    const step = (ts: number) => {
      if (!start) start = ts
      const k = Math.min(1, (ts - start) / ms)
      apply.current(from + (to - from) * ease(k))
      if (k < 1) { raf = requestAnimationFrame(step); return }
      if (closing) onExited?.()
    }
    raf = requestAnimationFrame(step)
    return () => cancelAnimationFrame(raf)
    // `onExited` is a fresh closure every render; the effect must run for the
    // OPEN/CLOSE transition only, or every parent render restarts the morph.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [closing])

  const title = folder.title || t('taskBar.folder')
  const sourceIdx = dragSourceKey ? folder.members.findIndex(m => `app:${m.kind}:${m.ref}` === dragSourceKey) : -1
  /** How far row `i` slides while a slot is open — same rule as the rail. */
  const shiftFor = (i: number) => {
    if (gapIndex === null || i === sourceIdx) return 0
    const j = sourceIdx >= 0 && i > sourceIdx ? i - 1 : i
    return (j >= gapIndex ? 1 : 0) - (sourceIdx >= 0 && i > sourceIdx ? 1 : 0)
  }

  return createPortal(
    <div
      ref={panelRef}
      data-folder-popup={folder.id}
      role="dialog"
      aria-label={title}
      // ws-clear: the theme class paints an opaque --bg-primary, which would
      // sit as a rectangle behind the silhouette; the SVG paints the surface.
      // The BOX is a rectangle from the tile's left edge; the SILHOUETTE is not.
      // Above and below the tile the box covers the rail's neighbouring tiles,
      // so the root is click-through and only the painted shape, the tile face
      // and the panel content take the pointer — a press on the tile above
      // still starts a drag, and still reads as "outside" to the dismiss.
      className={`${themeClass} ws-clear ws-folder-island themed-text pointer-events-none`}
      style={{ position: 'fixed', left: layout.left, top: layout.top, width: layout.width, height: layout.height, zIndex: 40 }}
    >
      {/* The silhouette. Its `d`, and the box around it, are rewritten every
          frame of the open and the close — see "How it opens and closes". */}
      <svg ref={svgRef} className="absolute inset-0 pointer-events-none" width={layout.width} height={layout.height} viewBox={`0 0 ${layout.width} ${layout.height}`} aria-hidden>
        <path ref={shapeRef} className={`ws-folder-island-shape ${closing ? '' : 'pointer-events-auto'}`} d={islandPath(layout)} />
      </svg>

      <div ref={contentRef} className={`absolute flex flex-col ${closing ? '' : 'pointer-events-auto'}`} style={{ left: layout.tile, top: 0, width: PANEL_W, height: layout.height, padding: PAD }}>
        <div className="px-2 flex items-center shrink-0" style={{ height: HEADER_H }}>
          {renaming
            ? <RenameInput initial={folder.title} placeholder={t('taskBar.folderNamePlaceholder')} onCommit={onRename} onCancel={onRenameCancel} />
            : (
              // Double-click to rename, the same shortcut the chat's session
              // tabs already use — so it is this app's established gesture
              // rather than a new one. The context menu stays the discoverable
              // path; this is the fast one for someone who already knows.
              // `select-none` because a double-click otherwise selects the word
              // before the input replaces it, which flashes a highlight.
              <div
                className="text-xs font-semibold uppercase tracking-wide themed-text-muted truncate select-none cursor-text"
                title={title}
                onDoubleClick={onStartRename}
              >{title}</div>
            )}
        </div>
        <div data-folder-list className="relative flex-1 min-h-0 overflow-y-auto overscroll-contain flex flex-col">
          {folder.members.map((app, i) => {
            const key = `${app.kind}:${app.ref}`
            const shift = shiftFor(i)
            return (
              <button
                key={key}
                type="button"
                data-folder-member={key}
                data-drag-source={dragSourceKey === `app:${key}` ? (dragSettling ? 'settling' : 'true') : undefined}
                style={{ height: FOLDER_ROW_H, transform: shift ? `translateY(${shift * FOLDER_ROW_H}px)` : undefined, transition: dragSourceKey && !dragSettling ? RAIL_SHIFT_TRANSITION : undefined }}
                // Record the launch from the RESULT, not from the attempt.
                // Setting it before the call marked a swallowed drag-click as a
                // launch, and since nothing reset it, focus restore stayed
                // disabled for the life of that island — so a later Escape left
                // focus on a removed node instead of the rail tile.
                onClick={() => { openedApp.current = onOpenApp(app) !== false }}
                onContextMenu={(e) => { e.preventDefault(); onAppContextMenu(app, e.currentTarget) }}
                onPointerDown={onMemberPointerDown ? (e) => onMemberPointerDown(app, e) : undefined}
                className={`group relative flex items-center gap-2.5 pl-5 pr-2 rounded-lg text-left text-sm transition-colors hover:themed-bg-tertiary shrink-0 cursor-grab active:cursor-grabbing ${
                  app.active ? 'themed-bg-tertiary' : ''
                }`}
                title={`${app.title || t('taskBar.app')} — ${t('taskBar.dragHint')}`}
              >
                <GripVertical size={12} className="ws-rail-grip absolute left-1 themed-text-muted" aria-hidden />
                <span className="shrink-0 flex items-center justify-center" style={{ width: ROW_ICON, height: ROW_ICON }}>
                  {app.kind === 'miniapp'
                    ? <MiniappIcon spec={app.icon ?? null} name={app.title || t('taskBar.app')} size={ROW_ICON} refId={app.ref} rev={app.iconRev ?? null} className="pointer-events-none" />
                    : <Globe size={18} className="themed-icon pointer-events-none" />}
                </span>
                <span className="truncate flex-1">{app.title || t('taskBar.app')}</span>
                {/* Same vocabulary as the rail: a dot for running. */}
                {app.running && <span aria-hidden className="w-1.5 h-1.5 rounded-full bg-brand shrink-0" />}
              </button>
            )
          })}
          {gapIndex !== null && (
            <div aria-hidden className="ws-rail-landing" style={{ left: 4, right: 4, top: gapIndex * FOLDER_ROW_H + 2, height: FOLDER_ROW_H - 4 }}>
              {landingGhost}
            </div>
          )}
        </div>
      </div>

      {/* The island's own tile face, over the rail's tile: closes the folder, and
          is where an app dragged onto the OPEN folder lands. */}
      <button
        type="button"
        ref={(el) => { faceRef.current = el; tileFaceRef?.(el) }}
        data-island-tile={folder.id}
        data-drop-armed={armed ? 'true' : undefined}
        onClick={onClose}
        aria-label={t('taskBar.close')}
        className={`absolute flex items-center justify-center rounded-l-lg transition-transform ${closing ? '' : 'pointer-events-auto'}`}
        style={{ left: 0, top: layout.tileTop, width: layout.tile, height: layout.tile }}
      >
        <FolderGlyph members={folder.members} color={folder.color} fallbackName={t('taskBar.app')} />
      </button>

    </div>,
    document.body,
  )
}

function RenameInput({ initial, placeholder, onCommit, onCancel }: {
  initial: string; placeholder: string; onCommit: (title: string) => void; onCancel: () => void
}) {
  const [value, setValue] = useState(initial)
  const ref = useRef<HTMLInputElement>(null)
  useEffect(() => { ref.current?.focus(); ref.current?.select() }, [])
  // Enter commits and the parent then unmounts this input, which fires blur —
  // so a commit must be one-shot or every rename is written twice.
  const done = useRef(false)
  const commit = () => { if (done.current) return; done.current = true; onCommit(value.trim().slice(0, 60)) }
  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') { e.preventDefault(); commit() }
    else if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); done.current = true; onCancel() }
  }
  return (
    <input
      ref={ref}
      value={value}
      placeholder={placeholder}
      maxLength={60}
      onChange={(e) => setValue(e.target.value)}
      onKeyDown={onKeyDown}
      onBlur={commit}
      // `outline-none`: what shows here is the BROWSER's default focus ring
      // (measured `outline: auto 1px rgb(0,95,204)`), not a border — the field
      // has `border-width: 0`. Dropping it is safe for this one input and not a
      // general licence to remove focus rings: it is auto-focused the moment it
      // appears, with its text SELECTED, and it is the only interactive thing
      // in rename mode — so focus stays obvious without it.
      className="themed-input w-full text-sm px-2 py-1 rounded-md outline-none focus:outline-none"
      aria-label={placeholder}
    />
  )
}
